import { useMemo } from "react";
import { useGetData } from "@/hooks/services/use-query-hooks";
import { GetTechnicianAvailability } from "@/hooks/services/pps/handlers/schedule-api";
import { GetMachineAvailability } from "@/hooks/services/pps/handlers/machines-api";
import type { ResourceAvailabilityResponse } from "@/hooks/services/pps/types/schedule.type";

export interface ShiftAvailability {
  am: boolean;
  pm: boolean;
  /** Reason the AM shift is unavailable (only meaningful when am === false). */
  amReason?: string | null;
  /** Reason the PM shift is unavailable (only meaningful when pm === false). */
  pmReason?: string | null;
  /** Whether OT (22:00–06:00) is AVAILABLE. OT defaults unavailable; true only when explicitly set. */
  ot?: boolean;
}

/** resourceId -> ("YYYY-MM-DD" -> {am, pm}). Only unavailable days are present. */
export type AvailabilityMap = Map<number, Map<string, ShiftAvailability>>;

const buildMap = (data?: ResourceAvailabilityResponse): AvailabilityMap => {
  const map: AvailabilityMap = new Map();
  for (const resource of data?.data ?? []) {
    const dayMap = new Map<string, ShiftAvailability>();
    for (const day of resource.days ?? []) {
      dayMap.set(day.date, {
        am: day.am,
        pm: day.pm,
        amReason: day.amReason,
        pmReason: day.pmReason,
        ot: day.ot
      });
    }
    map.set(resource.resourceId, dayMap);
  }
  return map;
};

/**
 * Bulk per-resource AM/PM availability for the job-scheduler calendar bands (WOR-2187).
 * One request per tab for the whole visible roster + range (no N+1). OT is excluded
 * server-side; absent days are fully available.
 */
export const useResourceAvailability = (
  kind: "technician" | "machine",
  ids: number[],
  startDate: string,
  endDate: string
): AvailabilityMap => {
  // Non-positive ids are client-side placeholders for resources that only exist as names
  // (e.g. AI Recommendation rows not matched to master data); the server has nothing for them.
  const resourceIds = useMemo(() => ids.filter((id) => id > 0), [ids]);
  const enabled = resourceIds.length > 0 && !!startDate && !!endDate;

  const handler =
    kind === "machine"
      ? GetMachineAvailability({ machineIds: resourceIds, startDate, endDate })
      : GetTechnicianAvailability({ technicianIds: resourceIds, startDate, endDate });

  const { data } = useGetData(handler, { enabled });

  return useMemo(() => buildMap(data), [data]);
};
